/* eslint-disable jsx-a11y/anchor-is-valid */

import React, { useState } from 'react';

import { CognitoUser } from 'amazon-cognito-identity-js';
import { SubmitHandler, useForm } from 'react-hook-form';

import {
  getCurrentUserAttributes,
  setNewPassword, signIn, SignInResult, verifyMFA,
} from '../../services/auth';
import { MFAMethod } from '../../services/auth/types';
import { getFormErrorMessage } from '../../utils/common/errorMessages';
import Button from '../Button';
import CollapsableNotification from '../CollapsableNotification';
import Link from '../Link';
import PasswordReset from '../PasswordReset';
import VerificationCode from '../VerificationCode';

import {
  ButtonContainer,
  FooterText,
  FormContainer,
  StyledSignIn,
  StyledTextField,
} from './index.styles';
import NewPasswordForm from './NewPasswordForm';
import TOTPSetupForm from './TOTPSetupForm';
import { SignInPageState } from './types';

interface FormValues {
  email: string;
  password: string;
}

interface SignInProps {
  onAuthenticate: () => void;
  onUnconfirmedSignIn?: ((email: string, password: string) => void);
}

const SignIn = ({ onAuthenticate, onUnconfirmedSignIn } : SignInProps) => {
  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>();
  const [errorOpen, setErrorOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [formState, setFormState] = useState<SignInPageState>(SignInPageState.SignIn);
  const [data, setData] = useState<FormValues>({ email: '', password: '' });
  const [authUser, setAuthUser] = useState<CognitoUser | null>(null);
  const [mfaMethod, setMfaMethod] = useState<undefined | MFAMethod>(undefined);

  const onSuccess = () => {
    setLoading(false);
    onAuthenticate();
  };

  const onFailure = (d: FormValues, err: any) => {
    setLoading(false);
    if (err?.code === 'UserNotConfirmedException' && onUnconfirmedSignIn) {
      // If the user has signed up, but has not yet confirmed their number,
      // redirect them to finish sign up
      setData({ email: d.email, password: d.password });
      onUnconfirmedSignIn(d.email, d.password);
    } else {
      setErrorMessage(err?.data?.message || err?.message || 'An error has occurred');
      setErrorOpen(true);
    }
  };

  const handlePostSignIn = async (signInRes: SignInResult) => {
    const { user, mfaRequired, newPasswordRequired } = signInRes;

    if (newPasswordRequired) {
      setAuthUser(user);
      setFormState(SignInPageState.NewPassword);
    } else if (mfaRequired) {
      setAuthUser(user);
      setMfaMethod(signInRes?.mfaMethod);
      setFormState(SignInPageState.MFA);
    } else {
      onSuccess();
    }
  };

  // Attempts sign in and handles the scenario where MFA is required
  const attemptSignIn = async (email: string, password: string) => {
    setData({ email, password });
    try {
      const signInRes = await signIn(email, password);
      await handlePostSignIn(signInRes);
    } catch (err) {
      onFailure({ email, password }, err);
    }
  };

  const onSubmit: SubmitHandler<FormValues> = async (d: FormValues) => {
    setLoading(true);
    await attemptSignIn(d.email, d.password);
  };

  const SignInForm = (
    <>
      <CollapsableNotification
        open={errorOpen}
        close={() => setErrorOpen(false)}
        message={errorMessage}
      />
      <FormContainer onSubmit={handleSubmit(onSubmit)}>
        <StyledTextField
          placeholder="Email"
          data-cy="email"
          {...register('email', { required: true, pattern: /^\S+@\S+$/i })}
          error={!!errors.email}
          errorMessage={errors.email ? getFormErrorMessage(errors.email.type, 'email') : ''}
        />
        <StyledTextField
          placeholder="Password"
          data-cy="password"
          type="password"
          {...register('password', { required: true })}
          error={!!errors.password}
          errorMessage={errors.password ? getFormErrorMessage(errors.password.type, 'password') : ''}
        />
        <ButtonContainer>
          <Button
            variant="contained"
            type="submit"
            loading={loading}
          >
            Sign In
          </Button>
        </ButtonContainer>
        <FooterText>Forgot password?&nbsp;
          <Link
            data-cy="passwordReset"
            component="button"
            type="button"
            onClick={() => {
              setFormState(SignInPageState.PasswordReset);
            }}
            underline="always"
          >
            Reset Password
          </Link>
        </FooterText>
      </FormContainer>
    </>
  );

  const PasswordResetForm = (
    <>
      <PasswordReset
        onComplete={() => setFormState(SignInPageState.SignIn)}
      />
      <FooterText>No need to reset?&nbsp;
        <Link
          component="button"
          variant="body1"
          onClick={() => setFormState(SignInPageState.SignIn)}
          underline="always"
          type="button"
        >
          Return to Sign In
        </Link>
      </FooterText>
    </>
  );

  const MFAForm = (
    <VerificationCode
      source={mfaMethod === MFAMethod.SMS ? 'mobile phone' : 'authenticator'}
      confirm={async ({ verification }) => authUser && mfaMethod && verifyMFA(
        authUser, mfaMethod, verification)}
      resend={mfaMethod === MFAMethod.SMS ? () => {
        attemptSignIn(data.email, data.password);
      } : undefined}
      onConfirm={async () => {
        const currUserAttributes = await getCurrentUserAttributes();
        const totpEnabled = currUserAttributes?.find((attr) => attr.Name === 'custom:totpEnabled')?.Value === 'true';
        const totpSetup = currUserAttributes?.find((attr) => attr.Name === 'custom:totpSetup')?.Value === 'true';

        if (totpEnabled && !totpSetup) {
          // If TOTP is enabled but isn't set up yet, navigate to the TOTP set up page after sign in
          setFormState(SignInPageState.TOTPSetup);
        } else {
          // Otherwise, complete sign in
          onSuccess();
        }
      }}
    />
  );

  return (
    <StyledSignIn>
      {formState === SignInPageState.SignIn && SignInForm}
      {formState === SignInPageState.PasswordReset && PasswordResetForm}
      {formState === SignInPageState.MFA && mfaMethod && MFAForm}
      {formState === SignInPageState.NewPassword && (
        <NewPasswordForm
          onFormSubmit={async (newPassword) => {
            if (authUser) {
              const signInRes = await setNewPassword(authUser, newPassword);
              await handlePostSignIn(signInRes);
            }
          }}
        />
      )}
      {formState === SignInPageState.TOTPSetup && authUser && (
        <TOTPSetupForm
          user={authUser}
          email={data.email}
          onComplete={onSuccess}
        />
      )}
    </StyledSignIn>
  );
};

export default SignIn;
